import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Dumbbell, MapPin, MessageSquare, User, Shield, Bell, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import api from "@/lib/api";

const tabs = [
  { to: "/dashboard", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/workouts", label: "Workouts", icon: Dumbbell, testid: "nav-workouts" },
  { to: "/running", label: "Run", icon: MapPin, testid: "nav-run" },
  { to: "/coach", label: "Coach", icon: MessageSquare, testid: "nav-coach", premium: true },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export const BottomNav = () => {
  const { user } = useAuth();
  const active = user?.access?.active || user?.role === "admin";
  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/90 backdrop-blur"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="mx-auto flex max-w-[560px] items-stretch justify-between px-2">
        {tabs.map((t) => {
          const Icon = t.icon;
          const locked = t.premium && !active;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={({ isActive }) =>
                `relative flex flex-1 flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] transition-colors ${
                  isActive ? "text-[#ccff0c]" : "text-muted-foreground hover:text-muted-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {locked && (
                    <span className="absolute top-1.5 right-[calc(50%-18px)] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-secondary border border-border">
                      <Lock className="h-2 w-2 text-[#ccff0c]" />
                    </span>
                  )}
                  <Icon className={`h-5 w-5 ${isActive ? "scale-105" : ""} transition-transform`} strokeWidth={2.2} />
                  <span className="text-[10px] font-medium">{t.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const isAdmin = user?.role === "admin";
  const eligible = isAdmin || user?.subscription_tier === "coaching";
  const endpoint = isAdmin ? "/admin/coaching/unread" : "/coaching/unread";

  const fetchUnread = useCallback(() => {
    if (!eligible) return;
    api.get(endpoint).then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  }, [eligible, endpoint]);

  useEffect(() => {
    if (!eligible) return undefined;
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [eligible, fetchUnread]);

  if (!eligible) return null;

  const go = () => {
    setUnread(0);
    navigate(isAdmin ? "/admin?tab=coaching" : "/coaching");
  };

  return (
    <button data-testid="notification-bell" onClick={go} className="relative flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-[#ccff0c] hover:border-[#ccff0c]/40 transition-colors">
      <Bell className="h-5 w-5" />
      {unread > 0 && (
        <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ccff0c] text-black text-[10px] font-bold flex items-center justify-center">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </button>
  );
};

export const AppShell = ({ title, children, right, hideNav, noPad }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-[560px] items-center justify-between px-4 h-14">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2">
            <Logo className="h-8 w-8 rounded-lg" />
            <span className="font-display text-lg font-bold tracking-tight">
              <span className="text-foreground">REAL DEAL</span> <span className="rd-lime">FIT</span>
            </span>
          </button>
          <div className="flex items-center gap-2">
            <ThemeToggle testid="theme-toggle-header" />
            <NotificationBell />
            {user?.role === "admin" && (
              <button
                data-testid="header-admin-button"
                onClick={() => navigate("/admin")}
                className="flex items-center gap-1 rounded-full border border-[#ccff0c]/40 bg-[#ccff0c]/10 px-2.5 py-1 text-[#ccff0c] text-xs font-semibold hover:bg-[#ccff0c]/20 transition-colors"
              >
                <Shield className="h-3.5 w-3.5" /> Admin
              </button>
            )}
            {right}
          </div>
        </div>
        {title ? (
          <div className="mx-auto max-w-[560px] px-4 pb-3">
            <h1 className="font-display text-2xl font-bold">{title}</h1>
          </div>
        ) : null}
      </header>
      <main className={`mx-auto max-w-[560px] ${noPad ? "" : "px-4 py-4"} ${hideNav ? "" : "pb-28"}`}>
        {children}
      </main>
      {!hideNav && <BottomNav />}
    </div>
  );
};
